import { useState } from 'react';
import { MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { Menu, MenuItem } from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';

export interface ChatRowMenuProps {
  /** Names the menu for assistive tech, since the trigger is icon-only. */
  chatTitle: string;
  onRename: () => void;
  onDelete: () => void;
}

/**
 * Per-row rename/delete menu.
 *
 * Built on `Menu` rather than `Popover` because the panel has to escape the
 * sidebar: a `Popover` is an absolutely-positioned child, so every ancestor's
 * overflow and stacking context applies to it. `Menu` portals to the body and
 * positions against the trigger, so it is independent of where it is mounted.
 *
 * The trigger stays in the DOM at all times and is only visually hidden until
 * hover or focus — `opacity` rather than conditional rendering, so it remains
 * tabbable and screen-reader reachable when the pointer is nowhere near it.
 */
export function ChatRowMenu({ chatTitle, onRename, onDelete }: ChatRowMenuProps) {
  const [open, setOpen] = useState(false);

  return (
    <Menu
      open={open}
      onOpenChange={setOpen}
      side="right"
      align="start"
      aria-label={`Actions for ${chatTitle}`}
      contentClassName="w-[160px] ring-offset-v2-sidebar"
      trigger={
        <button
          type="button"
          aria-label={`Actions for ${chatTitle}`}
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            'relative grid size-6 shrink-0 place-items-center rounded-v2-sm text-v2-sidebar-foreground/60 before:absolute before:-inset-1 before:content-[\'\']',
            // Revealed on hover/focus for pointer+keyboard; always shown on touch,
            // which has neither, so the menu is reachable on a phone.
            'opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 [@media(pointer:coarse)]:opacity-100',
            'hover:bg-v2-sidebar-accent hover:text-v2-sidebar-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring',
            open && 'bg-v2-sidebar-accent opacity-100',
          )}
        >
          <MoreVertical size={14} />
        </button>
      }
    >
      <MenuItem label="Rename" onClick={onRename}>
        <Pencil size={14} className="shrink-0" />
        Rename
      </MenuItem>
      <MenuItem label="Delete" destructive onClick={onDelete}>
        <Trash2 size={14} className="shrink-0" />
        Delete
      </MenuItem>
    </Menu>
  );
}
